import { useEffect, useRef, type RefObject } from "react";

/** Physical print pages are measured from the saved, sanitized preview. No live form values or HTML enter here. */
export function PaginatedPrint({ source, section, identity, fields, stamp, attribution, revision, onReady }: {
  source: RefObject<HTMLElement | null>; section: string; identity: string;
  /** V1-INTERACTIONS §5.1: the instrument's boxed Candidate / Target role / Interviewer / Date block, on every
   * page. A JSON string rather than an array so re-rendering the dialog cannot re-trigger the measuring effect. */
  fields?: string | undefined;
  stamp: string;
  attribution?: string | undefined; revision: string; onReady: (ready: boolean) => void;
}) {
  const target = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    onReady(false);
    void document.fonts.ready.then(() => {
      if (cancelled || !source.current || !target.current) return;
      const host = target.current;
      host.replaceChildren();
      let fieldRows: { label: string; value: string }[] = [];
      try { fieldRows = fields ? (JSON.parse(fields) as { label: string; value: string }[]) : []; } catch { fieldRows = []; }
      const pages: HTMLElement[] = [];
      let content: HTMLElement;
      const node = (tag: string, className: string, text?: string) => {
        const el = document.createElement(tag); el.className = className;
        if (text !== undefined) el.textContent = text;
        return el;
      };
      const newPage = () => {
        const page = node("section", "physical-page");
        const header = node("header", "physical-header");
        // The client's instruments rule the page in red, then set "GoTo Foods | <SECTION>" left and "Page x of y" right.
        const title = node("div", "physical-title");
        title.append(node("span", "physical-section", `GoTo Foods | ${section}`), node("span", "physical-count", " Page 000 of 000"));
        header.append(title);
        if (fieldRows.length) {
          const block = node("div", "physical-fields");
          for (const field of fieldRows) {
            const cell = node("div", "physical-field");
            cell.append(node("div", "physical-field__label", field.label), node("div", "physical-field__value", field.value));
            block.append(cell);
          }
          header.append(block);
        } else header.append(node("div", "physical-identity", identity));
        header.append(node("div", "physical-stamp", stamp));
        if (attribution) header.append(node("div", "physical-attribution", attribution));
        // §5.1 keeps the saved version on paper; it rides the header so the footer carries his line and nothing else.
        header.append(node("div", "physical-revision", revision));
        content = node("div", "physical-body");
        const footer = node("footer", "physical-footer");
        footer.append(node("span", "physical-classification", "© GoTo Foods — Confidential, Internal Use Only"), node("span", "physical-section-label"));
        page.append(header, content, footer); host.append(page); pages.push(page);
      };
      const fits = () => content.scrollHeight <= content.clientHeight + 0.5;
      const clone = (el: Element) => el.cloneNode(true) as HTMLElement;
      // Slice text while preserving its safe markup. Unicode code points are kept intact at split boundaries.
      const fragment = (el: HTMLElement, start: number, end: number) => {
        const out = el.cloneNode(false) as HTMLElement;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const texts: Text[] = []; let next: Node | null;
        while ((next = walker.nextNode())) texts.push(next as Text);
        let offset = 0;
        for (const text of texts) {
          const size = text.data.length;
          if (start < offset + size && end > offset) {
            const range = document.createRange();
            range.setStart(text, Math.max(0, start - offset)); range.setEnd(text, Math.min(size, end - offset));
            // Each print field has simple inline labels/values; retaining that parent preserves their hierarchy.
            const parent = text.parentElement;
            if (parent && parent !== el) { const span = parent.cloneNode(false); span.appendChild(range.cloneContents()); out.appendChild(span); }
            else out.appendChild(range.cloneContents());
          }
          offset += size;
        }
        return out;
      };
      const add = (element: HTMLElement) => {
        content.append(element);
        if (fits()) return;
        element.remove();
        const heading = content.lastElementChild?.matches("h1,h2,h3") ? content.lastElementChild : null;
        heading?.remove();
        if (content.childNodes.length) newPage();
        if (heading) content.append(heading);
        content.append(element);
        if (fits()) return;
        element.remove();
        const text = element.textContent ?? "";
        let start = 0;
        while (start < text.length) {
          let low = start + 1, high = text.length, best = start;
          while (low <= high) {
            const mid = Math.floor((low + high) / 2), probe = fragment(element, start, mid);
            content.append(probe); const ok = fits(); probe.remove();
            if (ok) { best = mid; low = mid + 1; } else high = mid - 1;
          }
          if (best === start) throw new Error("A saved print block cannot fit at the readable font size.");
          if (best < text.length) {
            const space = text.lastIndexOf(" ", best);
            if (space > start && best - space < 80) best = space + 1;
            if (/^[\uDC00-\uDFFF]$/.test(text[best] ?? "")) best--;
          }
          content.append(fragment(element, start, best)); start = best;
          if (start < text.length) newPage();
        }
      };
      const addTable = (table: HTMLTableElement) => {
        let part: HTMLTableElement, body: HTMLTableSectionElement;
        const startTable = () => {
          part = table.cloneNode(false) as HTMLTableElement;
          if (table.tHead) part.append(clone(table.tHead));
          body = document.createElement("tbody"); part.append(body); content.append(part);
        };
        startTable();
        for (const row of [...table.tBodies].flatMap(group => [...group.rows])) {
          const copy = clone(row); body!.append(copy);
          if (fits()) continue;
          copy.remove();
          if (!body!.rows.length) part!.remove();
          if (content.childNodes.length) newPage();
          startTable(); body!.append(copy);
          if (fits()) continue;
          // An exceptionally long cell becomes labelled continuation fields instead of a clipped table row.
          part!.remove();
          [...row.cells].forEach((cell, index) => {
            const label = table.tHead?.rows[0]?.cells[index]?.textContent ?? `Column ${index + 1}`;
            const field = node("div", "print-line", `${label}: ${cell.textContent ?? ""}`); add(field);
          });
          newPage(); startTable();
        }
        if (!body!.rows.length) part!.remove();
      };
      const walk = (el: HTMLElement) => {
        if (el.tagName === "TABLE") { addTable(el as HTMLTableElement); return; }
        if (el.matches(".print-line, h1, h2, h3, p, .rating-ladder > div")) { add(clone(el)); return; }
        for (const child of el.children) walk(child as HTMLElement);
      };
      try {
        const saved = source.current.querySelector<HTMLElement>(".print-frame > tbody > tr > td");
        if (!saved) return;
        newPage();
        for (const item of saved.children) {
          if (item.matches(".print-page") && content!.childNodes.length) newPage();
          walk(item as HTMLElement);
        }
        if (!content!.childNodes.length && pages.length > 1) { pages.pop()!.remove(); }
        let label = "";
        pages.forEach((page, i) => {
          page.querySelector(".physical-count")!.textContent = ` Page ${i + 1} of ${pages.length}`;
          // A section that runs over keeps naming itself in the footer, the way his page families do.
          const heading = page.querySelector(".physical-body .print-section-name");
          if (heading) label = (heading.textContent ?? "").replace(/^[\s—·-]+|[\s—·-]+$/g, "");
          page.querySelector(".physical-section-label")!.textContent = label;
        });
        host.dataset.pages = String(pages.length); onReady(true);
      } catch {
        host.replaceChildren(); host.dataset.pages = "0"; onReady(false);
      }
    });
    return () => { cancelled = true; };
  }, [source, section, identity, fields, stamp, attribution, revision, onReady]);
  return <div className="print-pagination" ref={target} aria-hidden="true" />;
}
